import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  Button,
  Form,
  Grid,
  Icon,
  Message,
  Modal,
  Transition,
  Responsive
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import * as yup from 'yup';
import Svg from 'react-inlinesvg';
import { Routes } from '../routing';
import { editIcon, triangleIcon } from '../images';
import { FormComponents, StreamModal } from '../components';
import { Breakpoints, GridNumbers, Formatters } from '../utils';
import { getErrorMessage } from '../utils/validations';
import { authProptypes } from '../auth';
import restFormSubmitAPI from '../api/restFormSubmitAPIs';
import stateProvince from '../data/state-province-list.json';
import { TOOLS } from '../constants';

/**
 * static brands based on https://applydigital.atlassian.net/wiki/spaces/ADS/pages/313065582/Warranty+Claims+Form+Fields+Reconciliation
 * (original requirements https://applydigital.atlassian.net/browse/AS-845)
 */
const handlingBrands = [
  'batco',
  'hutchinson',
  'mayrath',
  'rem',
  'storm',
  'westfield',
  'wheatheart'
];

const WarrantyClaimsForm = ({
  intl,
  navigator,
  analytics,
  authUser,
  authSession,
  brandsList
}) => {
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDealerFormOpen, setIsDealerFormOpen] = useState(true);

  const dealership = {
    dealer_name: authUser.dealer.name || '',
    dealer_contact: `${authUser.givenName} ${authUser.lastName}` || '',
    dealer_address: authUser.primaryAddress.street || '',
    dealer_city_town: authUser.primaryAddress.city || '',
    dealer_state_province: authUser.primaryAddress.stateCode || '',
    dealer_zip_postal: authUser.primaryAddress.postalCodeString || '',
    dealer_phone: authUser.primaryAddress.phoneString || '',
    dealer_email: authUser.email || ''
  };

  const initialValues = {
    ...dealership,
    dealership,

    customer_name: '',
    customer_address: '',
    customer_city_town: '',
    customer_state_province: '',
    customer_zip_postal: '',
    customer_phone: '',
    customer_email: '',

    brand: brandsList.length === 1 ? brandsList[0].name : '',
    dealer_no: '',
    model: '',
    serial_number: '',
    date_occurred: '',
    customer_invoice_no: '',
    date_purchased: '',
    dealer_invoice_no: '',
    length: '',
    estimated_usage_hours: '',
    describe_breakdown: '',
    photos: []
  };

  /*phone number regex as per AGIDS-200.
      # Masking in `(XXX) XXX-XXXX` format
      # Max. 14 digits including brackets and hyphens
      # Assuming only NORTH AMERICAN NUMBER
  */
  const phoneNumberRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
  // As per AS-1272 the product and QA team wants some fields to only have alphanumeric value.
  const alphaNumericRegex = /^[A-Za-z0-9]+$/;
  const translated = (key, max, min) =>
    getErrorMessage({ key, values: { max, min } });

  const genericOptional = yup
    .string()
    .trim()
    .max(100, translated('max_length', 100));
  const genericValidation = genericOptional.required(translated('required'));

  const dealershipSchema = {
    dealer_name: genericValidation,
    dealer_contact: genericValidation,
    dealer_address: yup
      .string()
      .trim()
      .max(250, translated('max_length', 250))
      .required(translated('required')),
    dealer_city_town: genericValidation,
    dealer_state_province: genericValidation,
    dealer_zip_postal: genericValidation,
    dealer_phone: genericValidation.matches(
      phoneNumberRegex,
      translated('phone')
    ),
    dealer_email: genericValidation.email(translated('email'))
  };

  let validationSchema = {
    ...dealershipSchema,

    dealership: yup.object().shape(dealershipSchema),

    customer_name: genericValidation,
    customer_address: yup
      .string()
      .trim()
      .max(250, translated('max_length', 250)),
    customer_city_town: genericOptional,
    customer_state_province: genericOptional,
    customer_zip_postal: genericOptional,
    customer_phone: genericValidation.matches(
      phoneNumberRegex,
      translated('phone')
    ),
    customer_email: genericOptional.email(translated('email')),

    brand: genericValidation,
    dealer_no: genericValidation,
    model: genericValidation,
    serial_number: genericValidation.matches(
      alphaNumericRegex,
      translated('alphanumeric')
    ),
    customer_invoice_no: genericValidation.matches(
      alphaNumericRegex,
      translated('alphanumeric')
    ),
    date_purchased: yup
      .date()
      .typeError(translated('date_invalid'))
      .max(new Date(), translated('date_max', 'today'))
      .required(translated('required')),
    dealer_invoice_no: genericValidation.matches(
      alphaNumericRegex,
      translated('alphanumeric')
    ),
    date_occurred: yup
      .date()
      .typeError(translated('date_invalid'))
      .max(new Date(), translated('date_max', 'today'))
      .min(yup.ref('date_purchased'), ({ min }) =>
        translated('date_min', null, Formatters.date(min, 'MM/DD/YYYY'))
      )
      .required(translated('required')),
    describe_breakdown: yup
      .string()
      .max(65535, translated('maxLimit'))
      .required(translated('required'))
  };

  // when selected brand matches, add extravalidations
  if (handlingBrands.includes(selectedBrand)) {
    validationSchema = {
      ...validationSchema,
      length: genericValidation,
      estimated_usage_hours: genericValidation
    };
  }

  // Track warranty claims submission on MixPanel AS-903
  const trackSubmitAnalytics = values => {
    analytics.trackEvent('Warranty Claim Submitted', {
      Tool: TOOLS.WARRANTY_CLAIM,
      'Number of Images Attached': values.photos ? values.photos.length : 0,
      Brand: values.brand,
      'Product Model': values.model,
      'Dealer Name': values.dealer_name
    });

    analytics.trackFormSubmission({
      Tool: TOOLS.WARRANTY_CLAIM,
      Brand: values.brand
    });
  };

  // Handles submission of form. If successful, calls setIsCompleted to display message.
  // eslint-disable-next-line no-unused-vars
  const onSubmit = async ({ dealership, ...values }, FormikBag) => {
    const { setSubmitting, setFieldTouched, setStatus } = FormikBag;
    setStatus({ error: null });

    try {
      // add this property to the form values we want to submit to the server.
      // this is needed to the server can return a list os S3 signed URLs for image upload.
      // exclude the actual photos property because we aren't submitting it to the server.
      const { photos, ...rest } = values;
      const valuesToSubmit = {
        imageFileTypes: photos.map(x => x.type),
        ...rest
      };

      // the submit API will return an array of signedURL for uploading imgs to S3 & a claimID.
      const {
        success,
        id,
        image_urls,
        errors
      } = await restFormSubmitAPI.submitWarrantyClaimForm(
        valuesToSubmit,
        authSession.accessToken
      );

      // upload all images to S3: called after sucess with photos
      const uploadImages = async () => {
        if (image_urls && id) {
          // TODO: there's no error handling here > what's expected??
          await Promise.all(
            image_urls.map((url, index) =>
              fetch(url, {
                method: 'PUT',
                headers: {
                  'Content-Type': photos[index].type
                },
                body: photos[index]
              })
            )
          );
        } else {
          // NOTE: throwing error here does nothing for the user
          // If there are photos to upload, make sure we have signed urls returned by the server
          throw new Error(
            'No S3 signed urls or submited claim id were returned'
          );
        }
      };

      let error = null;
      if (success) {
        if (photos.length) {
          uploadImages();
        }
        // open confirmation modal
        setIsModalOpen(true);
        trackSubmitAnalytics(values);
      } else {
        error =
          errors &&
          errors.map((err, i) => (
            <Fragment key={i}>
              {err}
              <br />
            </Fragment>
          ));
        // open dealership info form just in case :)
        setIsDealerFormOpen(true);
      }

      setStatus({ error });
      setSubmitting(false);
    } catch (error) {
      // setting generic error message instead of error
      setStatus({
        error: (
          <FormattedMessage
            id="error.generic"
            defaultMessage="Something went wrong."
          />
        )
      });
      setSubmitting(false);
      Object.keys(values).forEach(key => {
        setFieldTouched(key, false);
      });
    }
  };

  return (
    <Formik
      validationSchema={yup.object().shape(validationSchema)}
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {({
        handleSubmit,
        isSubmitting,
        isValid,
        errors,
        touched,
        handleChange,
        status,
        setValues,
        setFieldValue,
        setFieldTouched,
        values,
        resetForm
      }) => {
        const renderDealerForm = () => (
          <section className="bg--secondary bg--padded">
            <header>
              <h2>
                <FormattedMessage
                  id="subtitle.dealership_info"
                  defaultMessage="Your Dealership Information"
                />
              </h2>
              {!isDealerFormOpen && (
                <a
                  onClick={() => setIsDealerFormOpen(true)}
                  className="text--nowrap link--icon"
                  data-qa="warranty-dealership-edit"
                >
                  <i className="icon">
                    <Svg src={editIcon} />
                  </i>
                  <Responsive
                    as={Fragment}
                    minWidth={Breakpoints.computerBreakpoint}
                  >
                    Edit
                  </Responsive>
                </a>
              )}
            </header>

            <Transition visible={isDealerFormOpen}>
              <header
                className={cn(
                  'transition--accordion',
                  isDealerFormOpen && 'is-active'
                )}
              >
                <Message
                  warning
                  visible
                  icon={
                    <Icon name="exclamation triangle">
                      <Svg src={triangleIcon} />
                    </Icon>
                  }
                  content={
                    <FormattedMessage
                      id="description.warranty_warning"
                      defaultMessage="Please note, that editing your dealership information will only affect this current claim. To make permanent changes please {contact}."
                      values={{
                        contact: (
                          <Link to={Routes.rep}>
                            <FormattedMessage
                              id="cta.contact_your_rep"
                              defaultMessage="contact your AGI rep"
                            />
                          </Link>
                        )
                      }}
                    />
                  }
                />
              </header>
            </Transition>
            <Transition visible={isDealerFormOpen}>
              <div
                className={cn(
                  'bg__inner bg__inner--narrow transition--accordion',
                  isDealerFormOpen && 'is-active'
                )}
              >
                <FormComponents.Field
                  type="text"
                  name="dealership.dealer_name"
                  label={
                    <FormattedMessage
                      id="form.dealership_name"
                      defaultMessage="Dealership Name"
                    />
                  }
                  onChange={handleChange}
                  error={errors.dealership && errors.dealership.dealer_name}
                />
                <FormComponents.Field
                  type="text"
                  name="dealership.dealer_contact"
                  label={
                    <FormattedMessage
                      id="form.dealership_contact"
                      defaultMessage="Dealership Contact"
                    />
                  }
                  onChange={handleChange}
                  error={errors.dealership && errors.dealership.dealer_contact}
                />
                <FormComponents.Field
                  type="text"
                  name="dealership.dealer_address"
                  label={
                    <FormattedMessage
                      id="form.address"
                      defaultMessage="Address"
                    />
                  }
                  onChange={handleChange}
                  error={errors.dealership && errors.dealership.dealer_address}
                />
                <FormComponents.Field
                  type="text"
                  name="dealership.dealer_city_town"
                  label={
                    <FormattedMessage
                      id="form.city_town"
                      defaultMessage="City"
                    />
                  }
                  onChange={handleChange}
                  error={
                    errors.dealership && errors.dealership.dealer_city_town
                  }
                />

                <Form.Group widths="equal">
                  <FormComponents.Select
                    type="text"
                    name="dealership.dealer_state_province"
                    placeholder="Select province/state"
                    options={stateProvince}
                    label={
                      <FormattedMessage
                        id="form.state_province"
                        defaultMessage="State/Province"
                      />
                    }
                    onChange={handleChange}
                    disabled={false}
                    error={
                      errors.dealership &&
                      errors.dealership.dealer_state_province
                    }
                  />
                  <FormComponents.Field
                    type="text"
                    name="dealership.dealer_zip_postal"
                    label={
                      <FormattedMessage
                        id="form.zip_postal"
                        defaultMessage="Zip/Postal Code"
                      />
                    }
                    onChange={handleChange}
                    error={
                      errors.dealership && errors.dealership.dealer_zip_postal
                    }
                    className="text--nowrap"
                    value={
                      values.dealership &&
                      values.dealership.dealer_zip_postal.toUpperCase()
                    }
                  />
                </Form.Group>
                <FormComponents.Field
                  type="tel"
                  name="dealership.dealer_phone"
                  label={
                    <FormattedMessage
                      id="form.phone"
                      defaultMessage="Telephone"
                    />
                  }
                  mask="(999) 999-9999"
                  placeholder="(XXX) XXX-XXXX"
                  onChange={handleChange}
                  error={errors.dealership && errors.dealership.dealer_phone}
                  value={values.dealership && values.dealership.dealer_phone}
                />
                <FormComponents.Field
                  type="text"
                  name="dealership.dealer_email"
                  label={
                    <FormattedMessage id="form.email" defaultMessage="Email" />
                  }
                  onChange={handleChange}
                  error={errors.dealership && errors.dealership.dealer_email}
                />
                <Form.Field>
                  <Button
                    secondary
                    size="small"
                    onClick={e => {
                      e.preventDefault();
                      setIsDealerFormOpen(false);
                      setValues({ ...values, ...values.dealership }, false);
                    }}
                    disabled={!!errors.dealership}
                    data-qa="warranty-claim-dealer-save"
                  >
                    Save
                  </Button>
                </Form.Field>
              </div>
            </Transition>

            <Transition visible={!isDealerFormOpen}>
              <Grid
                className={cn(
                  'transition--accordion',
                  !isDealerFormOpen && 'is-active'
                )}
              >
                <Grid.Column
                  as="dl"
                  mobile={GridNumbers.base}
                  tablet={GridNumbers.half}
                  computer={GridNumbers.half}
                >
                  <dt>
                    <h3>
                      <FormattedMessage
                        id="form.address"
                        defaultMessage="Address"
                      />
                    </h3>
                  </dt>
                  <dd>{values.dealer_name}</dd>
                  <dd>{values.dealer_contact}</dd>
                  <dd>{values.dealer_address}</dd>
                  <dd>
                    {values.dealer_city_town && `${values.dealer_city_town}, `}
                    {values.dealer_state_province}
                  </dd>
                  <dd>{values.dealer_zip_postal}</dd>
                </Grid.Column>
                <Grid.Column
                  as="dl"
                  mobile={GridNumbers.base}
                  tablet={GridNumbers.half}
                  computer={GridNumbers.half}
                >
                  <dt>
                    <h3>Contact</h3>
                  </dt>
                  <dd>{values.dealer_phone}</dd>
                  <dd className="text--wrap">{values.dealer_email}</dd>
                </Grid.Column>
              </Grid>
            </Transition>
          </section>
        );

        const renderCustomerForm = () => (
          <section className="bg--secondary bg--padded">
            <div className="bg__inner bg__inner--narrow">
              <header>
                <h2>
                  <FormattedMessage
                    id="subtitle.customer_info"
                    defaultMessage="Customer Information"
                  />
                </h2>
              </header>

              <FormComponents.Field
                type="text"
                name="customer_name"
                label={
                  <FormattedMessage
                    id="form.customer_name"
                    defaultMessage="Customer's Name"
                  />
                }
                onChange={handleChange}
                error={touched.customer_name && errors.customer_name}
              />
              <FormComponents.Field
                type="text"
                name="customer_address"
                label={`${intl.formatMessage({
                  id: 'form.address',
                  defaultMessage: 'Address'
                })} (${intl.formatMessage({
                  id: 'form.optional',
                  defaultMessage: 'optional'
                })})`}
                onChange={handleChange}
                error={touched.customer_address && errors.customer_address}
              />
              <FormComponents.Field
                type="text"
                name="customer_city_town"
                label={`${intl.formatMessage({
                  id: 'form.city_town',
                  defaultMessage: 'City'
                })} (${intl.formatMessage({
                  id: 'form.optional',
                  defaultMessage: 'optional'
                })})`}
                onChange={handleChange}
                error={touched.customer_city_town && errors.customer_city_town}
              />
              <Form.Group widths="equal">
                <FormComponents.Select
                  type="text"
                  name="customer_state_province"
                  placeholder="Select"
                  options={stateProvince}
                  placeholderDisabled={false}
                  label={`${intl.formatMessage({
                    id: 'form.state_province',
                    defaultMessage: 'State/Province'
                  })} (${intl.formatMessage({
                    id: 'form.optional',
                    defaultMessage: 'optional'
                  })})`}
                  onChange={handleChange}
                  error={
                    touched.customer_state_province &&
                    errors.customer_state_province
                  }
                />
                <FormComponents.Field
                  type="text"
                  name="customer_zip_postal"
                  label={`${intl.formatMessage({
                    id: 'form.zip_postal',
                    defaultMessage: 'Zip/Postal Code'
                  })} (${intl.formatMessage({
                    id: 'form.optional',
                    defaultMessage: 'optional'
                  })})`}
                  onChange={handleChange}
                  error={
                    touched.customer_zip_postal && errors.customer_zip_postal
                  }
                  className="text--nowrap"
                  value={values && values.customer_zip_postal.toUpperCase()}
                />
              </Form.Group>
              <FormComponents.Field
                type="tel"
                name="customer_phone"
                label={
                  <FormattedMessage
                    id="form.phone"
                    defaultMessage="Telephone"
                  />
                }
                mask="(999) 999-9999"
                placeholder="(XXX) XXX-XXXX"
                onBlur={() => setFieldTouched('customer_phone', true)}
                onChange={handleChange}
                error={touched.customer_phone && errors.customer_phone}
                value={values && values.customer_phone}
              />
              <FormComponents.Field
                type="text"
                name="customer_email"
                label={`${intl.formatMessage({
                  id: 'form.email',
                  defaultMessage: 'Email'
                })} (${intl.formatMessage({
                  id: 'form.optional',
                  defaultMessage: 'optional'
                })})`}
                onChange={handleChange}
                error={touched.customer_email && errors.customer_email}
              />
            </div>
          </section>
        );

        const renderItemInfoForm = () => (
          <Fragment>
            <FormComponents.Field
              type="text"
              name="dealer_no"
              label={
                <FormattedMessage
                  id="form.dealer_account_no"
                  defaultMessage="Dealer Account #"
                />
              }
              onChange={handleChange}
              error={touched.dealer_no && errors.dealer_no}
            />
            <FormComponents.Field
              type="text"
              name="model"
              label={
                <FormattedMessage id="form.model" defaultMessage="Model" />
              }
              onChange={handleChange}
              error={touched.model && errors.model}
            />
            <FormComponents.Field
              type="text"
              name="serial_number"
              label={
                <FormattedMessage
                  id="form.serial_number"
                  defaultMessage="Serial Number"
                />
              }
              onChange={handleChange}
              error={touched.serial_number && errors.serial_number}
              value={values && values.serial_number.toUpperCase()}
            />
            <FormComponents.Field
              type="text"
              name="customer_invoice_no"
              label={
                <FormattedMessage
                  id="form.customer_invoice_no"
                  defaultMessage="Customer Invoice #"
                />
              }
              onChange={handleChange}
              error={touched.customer_invoice_no && errors.customer_invoice_no}
            />
            <FormComponents.DateField
              name="date_purchased"
              label={
                <FormattedMessage
                  id="form.date_purchased"
                  defaultMessage="Date Purchased"
                />
              }
              // don't know why this needs `null` set even though component sets default `null`
              stored={values.date_purchased || null}
              onChange={date => {
                // reset date occurred value
                setFieldValue('date_occurred', '');
                setFieldValue('date_purchased', date);
              }}
              onBlur={() => setFieldTouched('date_purchased', true)}
              error={touched.date_purchased && errors.date_purchased}
              disabledDays={[{ after: new Date() }]}
              locale={intl.locale}
            />
            <FormComponents.Field
              type="text"
              name="dealer_invoice_no"
              label={
                <FormattedMessage
                  id="form.dealer_invoice_no"
                  defaultMessage="Dealer Invoice #"
                />
              }
              onChange={handleChange}
              error={touched.dealer_invoice_no && errors.dealer_invoice_no}
            />
          </Fragment>
        );

        const renderExtraFields = () => (
          <Fragment>
            <FormComponents.Field
              type="text"
              name="length"
              label={
                <FormattedMessage id="form.length" defaultMessage="Length" />
              }
              onChange={handleChange}
              error={touched.length && errors.length}
              qa="item-length-field"
            />
            <FormComponents.Field
              type="text"
              name="estimated_usage_hours"
              label={
                <FormattedMessage
                  id="form.estimated_usage_hours"
                  defaultMessage="Estimated Usage Hours"
                />
              }
              onChange={handleChange}
              error={
                touched.estimated_usage_hours && errors.estimated_usage_hours
              }
            />
          </Fragment>
        );

        const renderItemForm = () => (
          <section className="bg--secondary bg--padded">
            <div className="bg__inner bg__inner--narrow">
              <header>
                <h2>
                  <FormattedMessage
                    id="subtitle.item_info"
                    defaultMessage="Item Information"
                  />
                </h2>
              </header>
              <FormComponents.Select
                name="brand"
                placeholder={`${intl.formatMessage({
                  id: 'form.select',
                  defaultMessage: 'Select'
                })} ${intl.formatMessage({
                  id: 'form.brand',
                  defaultMessage: 'Brand'
                })}`}
                label="Brand"
                options={
                  brandsList &&
                  brandsList.map(brand => ({
                    value: brand.name,
                    text: brand.name
                  }))
                }
                disabled={brandsList.length === 1}
                onChange={e => {
                  const {
                    target: { value }
                  } = e;
                  handleChange(e);
                  setSelectedBrand(value.toLowerCase());
                }}
                error={touched.brand && errors.brand}
              />
              <Transition visible={!!values.brand}>
                <div
                  className={cn(
                    'transition--accordion',
                    !!values.brand && 'is-active'
                  )}
                >
                  {values.brand && renderItemInfoForm()}
                  {handlingBrands.includes(values.brand.toLowerCase()) &&
                    renderExtraFields()}
                </div>
              </Transition>
            </div>
          </section>
        );

        const renderDescriptionForm = () => (
          <section className="bg--secondary bg--padded">
            <div className="bg__inner bg__inner--narrow">
              <header>
                <h2>
                  <FormattedMessage
                    id="form.describe_breakdown"
                    defaultMessage="Description of Issue"
                  />
                </h2>
              </header>
              <FormComponents.DateField
                name="date_occurred"
                label={
                  <FormattedMessage
                    id="form.date_failure_occurred"
                    defaultMessage="Date of incident"
                  />
                }
                stored={values.date_occurred || null}
                onChange={date => setFieldValue('date_occurred', date)}
                onBlur={() => setFieldTouched('date_occurred', true)}
                error={touched.date_occurred && errors.date_occurred}
                disabled={!values.date_purchased || !!errors.date_purchased}
                disabledDays={[
                  {
                    before:
                      values.date_purchased && new Date(values.date_purchased)
                  }
                ]}
                locale={intl.locale}
              />
              <FormComponents.Textarea
                name="describe_breakdown"
                label={
                  <FormattedMessage
                    id="form.describe_breakdown"
                    defaultMessage="Description of the issue"
                  />
                }
                onChange={handleChange}
                error={touched.describe_breakdown && errors.describe_breakdown}
                qa="description-description-issue-field"
              />
            </div>
            <div className="bg__inner">
              <FormComponents.ImagesField
                name="photos"
                label="Photos of the issue (5 maximum)"
                onChange={photos => setFieldValue('photos', photos)}
                error={touched.photos && errors.photos}
                qa="description-photo-field"
                value={values.photos}
              />
            </div>
          </section>
        );

        const renderModal = () => (
          <StreamModal
            isOpen={isModalOpen}
            toggleModal={setIsModalOpen}
            size="tiny"
            insetClose={true}
            data-qa="warranty-claim-success-modal"
          >
            <Modal.Content>
              <Modal.Description>
                <h3>Your claim has been submitted successfully.</h3>
                <p>
                  This claim has been automatically emailed to you and your
                  customer.
                </p>
                <Form.Field inline>
                  <Button
                    primary
                    size="small"
                    onClick={() => {
                      resetForm(initialValues);
                      setIsModalOpen(false);
                      window && window.scrollTo(0, 0);
                    }}
                    data-qa="warranty-claim-restart"
                  >
                    Make another claim
                  </Button>

                  <Button
                    secondary
                    size="small"
                    onClick={navigator.navigateHome}
                  >
                    Return to home
                  </Button>
                </Form.Field>
              </Modal.Description>
            </Modal.Content>
          </StreamModal>
        );

        /**
         * Rendering Form Grid
         */
        return (
          <Form
            onSubmit={handleSubmit}
            error={status && !!status.error}
            data-qa="warranty-claim-form"
          >
            <Grid reversed="computer">
              <Grid.Column
                mobile={GridNumbers.base}
                computer={GridNumbers.half}
              >
                {renderDealerForm()}
              </Grid.Column>

              <Grid.Column
                mobile={GridNumbers.base}
                computer={GridNumbers.half}
              >
                {renderCustomerForm()}
                {renderItemForm()}
                {renderDescriptionForm()}

                <FormComponents.FormError error={status && status.error} />
                <FormComponents.SubmitButton
                  submitting={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  qa={{ 'data-qa': 'warranty-claim-submit-button' }}
                >
                  <FormattedMessage id="cta.submit" defaultMessage="Submit" />
                </FormComponents.SubmitButton>
              </Grid.Column>
            </Grid>
            {renderModal()}
          </Form>
        );
      }}
    </Formik>
  );
};

WarrantyClaimsForm.propTypes = {
  error: PropTypes.string,
  intl: intlShape.isRequired,
  navigator: PropTypes.object,
  location: PropTypes.object,
  analytics: PropTypes.object,
  // from withAuth
  authUser: authProptypes.user,
  authSession: PropTypes.object,
  setIsCompleted: PropTypes.func,
  setSubmittedClaimId: PropTypes.func,
  brandsList: PropTypes.array
};

export default injectIntl(WarrantyClaimsForm);



// WEBPACK FOOTER //
// ./src/forms/WarrantyClaimsForm.js